import * as React from "react";
import {
  ColumnDef,
  useReactTable,
  getCoreRowModel,
  flexRender,
  RowSelectionState,
  VisibilityState,
} from "@tanstack/react-table";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

import { DataTablePagination } from "./data-table-pagination";
import { DataTableToolbar } from "./data-table-toolbar";

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  page: number;
  setPage: (page: number) => void;
  perPage: number;
  setPerPage: (size: number) => void;
  totalPages: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
  search: string;
  setSearch: (value: string) => void;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  lifecycleStage: string;
  setLifecycleStage: (value: string) => void;
  agencyFilter: string;
  setAgencyFilter: (value: string) => void;
  stateFilter: string;
  setStateFilter: (value: string) => void;
  userFilter: string;
  setUserFilter: (value: string) => void;
  agents: any[];
  isAdmin: boolean;
  isLoading?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  page,
  setPage,
  perPage,
  setPerPage,
  totalPages,
  hasNextPage,
  hasPrevPage,
  search,
  setSearch,
  statusFilter,
  setStatusFilter,
  lifecycleStage,
  setLifecycleStage,
  agencyFilter,
  setAgencyFilter,
  stateFilter,
  setStateFilter,
  userFilter,
  setUserFilter,
  agents,
  isAdmin,
  isLoading,
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>({});
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({
    agentName: isAdmin, // Show Agent column only for admins
  });

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: setRowSelection,
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      rowSelection,
      columnVisibility,
    },
  });

  // Extract selected rows data
  const selectedRows = table.getFilteredSelectedRowModel().rows;
  const selectedCount = selectedRows.length;
  const selectedIds = selectedRows.map(row => String((row.original as any).id));
  const selectedClients = selectedRows.map(row => row.original);

  return (
    <div className="space-y-2 mb-12">
      {/* ✅ Updated Toolbar with All Filters and Selection Actions */}
      <DataTableToolbar
        table={table}
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        lifecycleStage={lifecycleStage}
        setLifecycleStage={setLifecycleStage}
        agencyFilter={agencyFilter}
        setAgencyFilter={setAgencyFilter}
        stateFilter={stateFilter}
        setStateFilter={setStateFilter}
        userFilter={userFilter}
        setUserFilter={setUserFilter}
        agents={agents}
        isAdmin={isAdmin}
        selectedCount={selectedCount}
        selectedIds={selectedIds}
        selectedClients={selectedClients}
      />

      <div className="rounded-md border">
        <Table>
          {/* ✅ Table Headers */}
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHead key={header.id} colSpan={header.colSpan}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>

          {/* ✅ Table Body */}
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="text-center">
                  No results found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* ✅ Pagination Component */}
      <DataTablePagination
        page={page}
        setPage={setPage}
        perPage={perPage}
        setPerPage={setPerPage}
        totalPages={totalPages}
        hasNextPage={hasNextPage}
        hasPrevPage={hasPrevPage}
      />
    </div>
  );
}
